import React, { useCallback, useEffect, useState } from 'react';

import { DealId } from '@upstart/harmony/lib/legos/dealTypes';
import {
  FinanceApplication,
  FinanceResponse,
  FinanceResponseData,
} from '@upstart/harmony/lib/legos/financingTypes';

import WorkflowFinanceApplicationService, {
  RehashNotNeededResponse,
  RehashNotPossibleResponse,
  RehashPossibleResponse,
} from 'services/WorkflowFinanceApplicationService';

import CloseableSnackbar from 'components/CloseableSnackbar';
import RehashNotPossibleModal from './RehashNotPossibleModal';
import RehashPossibleModal from './RehashPossibleModal';
import { RehashOfferSelection } from './RehashPossibleModal/RehashPossibleModal';
import useRehashOffers from './useRehashOffers';
import { amplitudeEvents } from '../../util/amplitudeEvents';
import { sendData } from '../../util/amplitude';

type RequiredFinanceFields = {
  data: Required<
    Pick<FinanceResponseData, 'apr' | 'monthly' | 'months' | 'downPayment'>
  >;
};

// Earlier in the flow, some of these fields may be undefined. However, at this point that should not be possible,
// and the fields are necessary to display the rehash possible modal.
export const isValidFinanceResponse = (
  response: FinanceResponse | undefined,
): response is FinanceResponse & RequiredFinanceFields => {
  if (response?.data === undefined) {
    return false;
  }

  return (
    response.data.apr !== undefined &&
    response.data.monthly !== undefined &&
    response.data.months !== undefined &&
    response.data.downPayment !== undefined
  );
};

const isRehashPossibleResponse = (
  response:
    | RehashPossibleResponse
    | RehashNotPossibleResponse
    | RehashNotNeededResponse,
): response is RehashPossibleResponse => response.result === 'rehash_possible';

const isRehashNotPossibleResponse = (
  response:
    | RehashPossibleResponse
    | RehashNotPossibleResponse
    | RehashNotNeededResponse,
): response is RehashNotPossibleResponse =>
  response.result === 'rehash_not_possible';

interface RehashOffersProps {
  dealId: DealId;
  financeApplications: FinanceApplication[] | null;
  financeResponses: FinanceResponse[] | null;
}

const RehashOffers: React.FC<RehashOffersProps> = ({
  financeApplications,
  financeResponses,
  dealId,
}) => {
  const [rehashPossibleOpen, setRehashPossibleOpen] = useState<boolean>(false);
  const [rehashNotPossibleOpen, setRehashNotPossibleOpen] = useState<boolean>(
    false,
  );

  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  const { rehashResult, originalOffer } = useRehashOffers(
    dealId,
    financeApplications,
    financeResponses,
  );

  useEffect(() => {
    switch (rehashResult.data.result) {
      case 'rehash_possible':
        setRehashPossibleOpen(true);
        sendData(amplitudeEvents.OFFER_REHASH_MODAL_OPEN, { dealId });
        break;
      case 'rehash_not_possible':
        setRehashNotPossibleOpen(true);
        sendData(amplitudeEvents.OFFER_REFRESH_MODAL_OPEN, { dealId });
        break;
      case 'rehash_not_needed':
        sendData(amplitudeEvents.OFFER_REHASH_NOT_NEEDED, { dealId });
        break;
    }
  }, [rehashResult]);

  const offersFound = useCallback(() => {
    // When the user clicks on the "Keep original offers" button, we want
    // to close the modal no matter what happens in Backend
    setSnackBarOpen(false);
    setRehashPossibleOpen(false);
    setRehashNotPossibleOpen(false);

    WorkflowFinanceApplicationService.offersFound(dealId).catch(() => {});
    sendData(amplitudeEvents.OFFER_REHASH_REFRESH_KEEP_ORIGINAL, { dealId });
  }, [dealId]);

  const handleRehashPossibleSelection = useCallback(
    (selectedOffer: RehashOfferSelection) => {
      switch (selectedOffer) {
        case 'original':
          offersFound();
          break;
        case 'rehashed':
          if (isRehashPossibleResponse(rehashResult.data)) {
            WorkflowFinanceApplicationService.rehashDeal(
              dealId,
              rehashResult.data.offerDetails,
            )
              .then(() => {
                sendData(amplitudeEvents.OFFER_REHASH_MODAL_CONTINUE, {
                  dealId,
                });
                setRehashPossibleOpen(false);
                setSnackbarMessage('Success! Upstart offer updated');
                setSnackBarOpen(true);
              })
              .catch(() => {
                setRehashPossibleOpen(false);
                setSnackbarMessage('An unknown error has occured');
                setSnackBarOpen(true);
              });
          }
          break;
      }
    },
    [rehashResult],
  );

  const refreshOffers = useCallback(() => {
    WorkflowFinanceApplicationService.refreshOffers(dealId)
      .then(() => {
        sendData(amplitudeEvents.OFFER_REFRESH_MODAL_CONTINUE, { dealId });
        setRehashNotPossibleOpen(false);
        setSnackbarMessage('Success! Upstart offer updated and ready to view');
        setSnackBarOpen(true);
      })
      .catch(() => {
        setRehashNotPossibleOpen(false);
        setSnackbarMessage('An unknown error has occured');
        setSnackBarOpen(true);
      });
  }, [dealId]);

  if (!isValidFinanceResponse(originalOffer)) {
    return null;
  }

  return (
    <>
      <CloseableSnackbar
        open={snackBarOpen}
        message={snackbarMessage}
        onClose={() => setSnackBarOpen(false)}
      />
      {isRehashNotPossibleResponse(rehashResult.data) && (
        <RehashNotPossibleModal
          isOpen={rehashNotPossibleOpen}
          reasons={rehashResult.data.reasons}
          keepOriginalOffers={offersFound}
          refreshOffers={refreshOffers}
        />
      )}
      {originalOffer && isRehashPossibleResponse(rehashResult.data) && (
        <RehashPossibleModal
          isOpen={rehashPossibleOpen}
          originalOffer={{
            monthlyPayment: originalOffer.data.monthly,
            apr: originalOffer.data.apr,
            contractMonths: originalOffer.data.months,
            downPayment: originalOffer.data.downPayment,
            accessoriesAndProducts: {
              selectedAccessories: originalOffer.data.selectedAccessories,
              selectedAddOns: originalOffer.data.selectedAddOns,
              selectedBundles: originalOffer.data.selectedBundles,
              selectedProducts: originalOffer.data.selectedProducts,
              protectionPlanProductPriceOverrides:
                originalOffer.data.protectionPlanProductPriceOverrides,
            },
          }}
          rehashedOffer={rehashResult.data.offerDetails}
          saveSelection={handleRehashPossibleSelection}
        />
      )}
    </>
  );
};

export default RehashOffers;
